import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Receipt, Plus, Edit2, Trash2, CheckCircle2, X, TrendingDown,
  ChevronDown, Search,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Textarea, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// OPEX — fixed monthly overheads
// =============================================================================
// Feeds the commercial dashboard's "monthly burn" number. Gives you one
// place to see all the company's fixed costs, grouped by category.
// Each item is normalised to a monthly cost regardless of its billing
// frequency (quarterly insurance → /3, annual → /12, etc).
// 23 Sep 2026 (mig 386): a fixed line can be the standing schedule for a
// premises XERO ACCOUNT (the rent line → 469 Rent), which is what Commercial →
// Premises forecasts from. Replaces 385's link to a premises budget code.
// =============================================================================

const FREQUENCIES = [
  { v: "weekly",      label: "Weekly",      monthly_mult: 52/12 },
  { v: "fortnightly", label: "Fortnightly", monthly_mult: 26/12 },
  { v: "monthly",     label: "Monthly",     monthly_mult: 1 },
  { v: "quarterly",   label: "Quarterly",   monthly_mult: 1/3 },
  { v: "annually",    label: "Annually",    monthly_mult: 1/12 },
  { v: "one_off",     label: "One-off",     monthly_mult: 0 },
] as const;

interface OpexItem {
  opex_id?: string;
  category_id: string | null;
  category_name?: string;
  item_name: string;
  supplier_id: string | null;
  supplier_name?: string;
  amount_ex_gst: number;
  gst_amount: number;
  frequency: string;
  start_date: string;
  end_date: string | null;
  next_due_date: string | null;
  payment_method: string | null;
  notes: string | null;
  monthly_cost_ex_gst?: number;
  is_active: boolean;
  // mig 386 — points this fixed line at the premises Xero ACCOUNT it is the
  // standing schedule for (rent → 469), so Commercial → Premises forecasts
  // from the lease rather than from last year's bills. 385's
  // premises_budget_code is still on the table, unused.
  premises_xero_account?: string | null;
}

export default function OpexScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canEdit = hasRole(user, "director");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<OpexItem> | null>(null);

  // -- Fetch items (via monthly view so we get the normalised monthly cost)
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["opex_items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_opex_monthly")
        .select("*")
        .order("monthly_cost_ex_gst", { ascending: false });
      if (error) throw error;
      // mig 386 — v_opex_monthly was created before premises_xero_account
      // existed, so the view doesn't carry it. Read it off the table and merge,
      // otherwise editing any line would silently clear its premises link.
      const { data: links } = await (supabase as any)
        .from("opex_items")
        .select("opex_id, premises_xero_account");
      const linkMap = new Map((links ?? []).map((l: any) => [l.opex_id, l.premises_xero_account]));
      return (data as any[]).map(i => ({ ...i, premises_xero_account: linkMap.get(i.opex_id) ?? null }));
    },
  });

  // mig 386 — the premises Xero accounts a fixed OPEX item can be the schedule
  // for: the bookkeeper's list (finance_settings) plus anything a premises bill
  // has actually hit. Balance-sheet accounts (the bond) are left out — a bond
  // is not a standing cost.
  const { data: premisesCodes = [] } = useQuery({
    queryKey: ["premises_accounts_for_opex"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("v_premises_accounts")
        .select("xero_account_code, xero_account_name, is_running_cost")
        .order("sort_order");
      return ((data ?? []) as any[]).filter(a => a.is_running_cost);
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["opex_categories"],
    queryFn: async () => {
      const { data } = await supabase.from("opex_categories").select("category_id, category_code, display_name").eq("is_active", true).order("sort_order");
      return data ?? [];
    },
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_opex"],
    queryFn: async () => {
      const { data } = await supabase.from("suppliers").select("supplier_id, legal_name").eq("is_active", true).order("legal_name");
      return data ?? [];
    },
  });

  // -- Filter
  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter(i =>
      (i.item_name ?? "").toLowerCase().includes(s)
      || (i.category_name ?? "").toLowerCase().includes(s)
      || (i.supplier_name ?? "").toLowerCase().includes(s)
    );
  }, [items, search]);

  // -- Group by category
  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const i of filtered) (g[i.category_name ?? "Uncategorised"] ||= []).push(i);
    return Object.entries(g).sort((a, b) => {
      const sumA = a[1].reduce((t: number, x: any) => t + (x.monthly_cost_ex_gst ?? 0), 0);
      const sumB = b[1].reduce((t: number, x: any) => t + (x.monthly_cost_ex_gst ?? 0), 0);
      return sumB - sumA;
    });
  }, [filtered]);

  // -- Burn totals
  const burn = useMemo(() => {
    const monthly = filtered.reduce((a, i) => a + (i.monthly_cost_ex_gst ?? 0), 0);
    return {
      monthly_ex: monthly,
      monthly_incl: monthly * 1.1,
      annual_ex: monthly * 12,
      weekly_ex: monthly * 12 / 52,
      items_count: filtered.length,
    };
  }, [filtered]);

  // -- Mutations
  const saveMut = useMutation({
    mutationFn: async (item: Partial<OpexItem>) => {
      const payload = {
        category_id: item.category_id || null,
        item_name: item.item_name,
        supplier_id: item.supplier_id || null,
        amount_ex_gst: Number(item.amount_ex_gst ?? 0),
        gst_amount: Number(item.gst_amount ?? 0),
        frequency: item.frequency,
        start_date: item.start_date,
        end_date: item.end_date || null,
        next_due_date: item.next_due_date || null,
        payment_method: item.payment_method || null,
        notes: item.notes || null,
        is_active: item.is_active ?? true,
        premises_xero_account: item.premises_xero_account || null,   // mig 386
      };
      if (item.opex_id) {
        const { error } = await (supabase as any).from("opex_items").update(payload).eq("opex_id", item.opex_id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any).from("opex_items").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opex_items"] });
      setEditing(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (opex_id: string) => {
      const { error } = await supabase.from("opex_items").update({ is_active: false }).eq("opex_id", opex_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opex_items"] }),
  });

  // -- Project costing rates (blanket labour $/hr + TA $/night — T-310 slice) --
  const { data: costRates = [] } = useQuery({
    queryKey: ["cost_rates"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from("cost_rate")
        .select("rate_type, amount, effective_from, created_at")
        .lte("effective_from", today)
        .order("effective_from", { ascending: false })
        .order("created_at", { ascending: false });
      return (data ?? []) as { rate_type: string; amount: number; effective_from: string }[];
    },
  });
  // costRates is sorted latest-first, so the first match per type is current.
  const labourRate = costRates.find(r => r.rate_type === "labour_hourly") ?? null;
  const taRate     = costRates.find(r => r.rate_type === "ta_nightly") ?? null;

  const [editingType, setEditingType] = useState<string | null>(null);
  const [newRate, setNewRate] = useState("");
  const setRateMut = useMutation({
    mutationFn: async (vars: { type: string; amount: number }) => {
      const { error } = await supabase.from("cost_rate").insert({
        rate_type: vars.type,
        amount: vars.amount,
        effective_from: new Date().toISOString().slice(0, 10),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cost_rates"] });
      setEditingType(null);
      setNewRate("");
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">OPEX Tracker</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every fixed monthly overhead — what the business burns through regardless of work volume.
          </div>
        </div>
        {canEdit && (
          <Button icon={Plus} onClick={() => setEditing({
            frequency: "monthly",
            start_date: new Date().toISOString().slice(0, 10),
            is_active: true,
          })}>
            New item
          </Button>
        )}
      </div>

      {/* Burn stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <KPI label="Monthly burn" value={fmtMoney(burn.monthly_ex)} sub="ex-GST" icon={TrendingDown} accent />
        <KPI label="Monthly (inc GST)" value={fmtMoney(burn.monthly_incl)} />
        <KPI label="Annual burn" value={fmtMoney(burn.annual_ex)} sub="ex-GST" />
        <KPI label="Active items" value={burn.items_count} icon={Receipt} />
      </div>

      {/* Project costing rates — blanket $/hr labour + $/night travel allowance,
          used to cost approved PROJECT timesheet hours/nights onto project
          budgets (budget_spend → project panel). Effective-dated: a change only
          affects future approvals. Labour → labour budget; TA → travel budget. */}
      <Card className="mb-5">
        <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-3">Project costing rates</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <RateRow
            label="Internal labour" unit="/hr" row={labourRate} canEdit={canEdit}
            editing={editingType === "labour_hourly"}
            onStart={() => { setNewRate(String(labourRate?.amount ?? "")); setEditingType("labour_hourly"); }}
            onCancel={() => setEditingType(null)}
            onSave={(v: number) => setRateMut.mutate({ type: "labour_hourly", amount: v })}
            saving={setRateMut.isPending} newRate={newRate} setNewRate={setNewRate}
          />
          <RateRow
            label="Travel allowance" unit="/night" row={taRate} canEdit={canEdit}
            editing={editingType === "ta_nightly"}
            onStart={() => { setNewRate(String(taRate?.amount ?? "")); setEditingType("ta_nightly"); }}
            onCancel={() => setEditingType(null)}
            onSave={(v: number) => setRateMut.mutate({ type: "ta_nightly", amount: v })}
            saving={setRateMut.isPending} newRate={newRate} setNewRate={setNewRate}
          />
        </div>
        <div className="text-[11px] text-text-dim mt-3">
          Costs approved <span className="text-text-muted">project</span> timesheet hours (→ labour budget) and away-nights (→ travel budget). Non-project time isn't costed. Changing a rate affects future approvals only.
        </div>
      </Card>

      {/* Per-category mini-widgets — shows monthly cost share for each
          category at a glance, sorted by spend. Click jumps to the section
          below. James 2026-04-25. */}
      {grouped.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2 px-1">
            By category
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {grouped.map(([cat, list]) => {
              const catTotal = list.reduce((a: number, x: any) => a + (x.monthly_cost_ex_gst ?? 0), 0);
              const pctOfBurn = burn.monthly_ex > 0 ? (catTotal / burn.monthly_ex) * 100 : 0;
              return (
                <a
                  key={cat}
                  href={`#opex-cat-${slug(cat)}`}
                  className="block bg-ink-raised border border-line rounded-md p-3 hover:border-accent transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-wider text-text-muted truncate" title={cat}>{cat}</div>
                  <div className="text-[15px] font-mono font-semibold text-text mt-1">{fmtMoney(catTotal)}</div>
                  <div className="text-[10px] text-text-dim font-mono mt-0.5">
                    {list.length} item{list.length === 1 ? "" : "s"} · {pctOfBurn.toFixed(0)}% of burn
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 max-w-md">
          <Search size={14} className="text-text-muted" />
          <input placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1" />
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner/></div>
      ) : grouped.length === 0 ? (
        <Card><EmptyState title="No OPEX items" description="Add your first fixed cost to get started." icon={Receipt} /></Card>
      ) : (
        grouped.map(([cat, list]) => {
          const catTotal = list.reduce((a, x) => a + (x.monthly_cost_ex_gst ?? 0), 0);
          // Pre-fill new-item editor with this category. Look up by display
          // name from the categories list (grouping key is the name string).
          const catRow = (categories as any[]).find(c => c.display_name === cat);
          return (
            <section key={cat} id={`opex-cat-${slug(cat)}`} className="mb-4 scroll-mt-4">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted mb-2 px-1 flex items-center justify-between gap-2">
                <span>{cat}</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-text">{fmtMoney(catTotal)}/mo</span>
                  {canEdit && (
                    <button
                      onClick={() => setEditing({
                        category_id: catRow?.category_id ?? null,
                        frequency: "monthly",
                        start_date: new Date().toISOString().slice(0, 10),
                        is_active: true,
                      })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium tracking-wider2 uppercase text-text-muted hover:text-accent hover:bg-ink-hover transition-colors"
                      title={`Add a new line to ${cat}`}
                    >
                      <Plus size={11}/> Add line
                    </button>
                  )}
                </div>
              </h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead><tr className="border-b border-line">
                      {["Item", "Supplier", "Amount", "Freq", "Monthly", "Notes", ""].map(h => (
                        <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {list.map((i, idx) => (
                        <tr key={i.opex_id} className={`border-b border-line last:border-b-0 ${idx % 2 ? "bg-ink/40" : ""}`}>
                          <td className="px-4 py-2">{i.item_name}</td>
                          <td className="px-4 py-2 text-[12px] text-text-muted">{i.supplier_name ?? "—"}</td>
                          <td className="px-4 py-2 font-mono">{fmtMoney(i.amount_ex_gst)}</td>
                          <td className="px-4 py-2 font-mono text-[11px] text-text-muted">{i.frequency.replace(/_/g, " ")}</td>
                          <td className="px-4 py-2 font-mono text-right">{fmtMoney(i.monthly_cost_ex_gst)}</td>
                          <td className="px-4 py-2 text-[11px] text-text-muted max-w-[200px] truncate">{i.notes ?? ""}</td>
                          {canEdit && (
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <button onClick={() => setEditing(i)} className="p-1.5 text-text-muted hover:text-text"><Edit2 size={12}/></button>
                              <button onClick={() => { if (confirm(`Remove ${i.item_name}?`)) deleteMut.mutate(i.opex_id); }} className="p-1.5 text-text-muted hover:text-danger"><Trash2 size={12}/></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>
          );
        })
      )}

      {editing && (
        <OpexEditor
          item={editing}
          categories={categories as any[]}
          suppliers={suppliers as any[]}
          premisesCodes={premisesCodes as any[]}
          onClose={() => setEditing(null)}
          onSave={saveMut.mutate}
          saving={saveMut.isPending}
        />
      )}
    </div>
  );
}

// slug — used for #opex-cat-{slug} anchors so per-category widgets can scroll
// to their section. Lowercase, alphanumeric + dash only.
function slug(s: string): string {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

// RateRow — one editable blanket cost rate (labour $/hr or TA $/night).
function RateRow({ label, unit, row, canEdit, editing, onStart, onCancel, onSave, saving, newRate, setNewRate }: any) {
  return (
    <div className="bg-ink-raised border border-line rounded-md p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">{label}</div>
          <div className="text-[20px] font-mono font-semibold text-text mt-0.5">
            {row ? fmtMoney(row.amount) : "—"}<span className="text-[12px] text-text-muted">{unit}</span>
          </div>
          {row?.effective_from ? (
            <div className="text-[10px] text-text-dim font-mono mt-0.5">Effective {fmtDate(row.effective_from)}</div>
          ) : null}
        </div>
        {canEdit && !editing && (
          <Button variant="secondary" size="sm" onClick={onStart}>Change</Button>
        )}
      </div>
      {canEdit && editing && (
        <div className="flex items-end gap-2 mt-3">
          <Field label={`New rate ${unit}`}>
            <Input type="number" step="0.01" mono value={newRate} onChange={(e: any) => setNewRate(e.target.value)} />
          </Field>
          <Button size="sm" icon={CheckCircle2} disabled={!newRate || saving} onClick={() => onSave(Number(newRate))}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function OpexEditor({ item, categories, suppliers, premisesCodes, onClose, onSave, saving }: any) {
  const [d, setD] = useState<Partial<OpexItem>>(item);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-ink-card border border-line w-full md:max-w-lg md:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-center justify-between">
          <h3 className="text-lg font-semibold">{item.opex_id ? "Edit" : "New"} OPEX item</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Item name *">
            <Input value={d.item_name ?? ""} onChange={e => setD({...d, item_name: e.target.value})} placeholder="e.g. Office rent — Lisson Grove" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={d.category_id ?? ""} onChange={e => setD({...d, category_id: e.target.value})}>
                <option value="">— none —</option>
                {categories.map((c: any) => <option key={c.category_id} value={c.category_id}>{c.display_name}</option>)}
              </Select>
            </Field>
            <Field label="Supplier">
              <Select value={d.supplier_id ?? ""} onChange={e => setD({...d, supplier_id: e.target.value})}>
                <option value="">— none —</option>
                {suppliers.map((s: any) => <option key={s.supplier_id} value={s.supplier_id}>{s.legal_name}</option>)}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Amount ex-GST *">
              <Input type="number" step="0.01" mono value={d.amount_ex_gst ?? ""} onChange={e => setD({...d, amount_ex_gst: Number(e.target.value)})} />
            </Field>
            <Field label="GST">
              <Input type="number" step="0.01" mono value={d.gst_amount ?? ""} onChange={e => setD({...d, gst_amount: Number(e.target.value)})} />
            </Field>
            <Field label="Frequency *">
              <Select value={d.frequency ?? "monthly"} onChange={e => setD({...d, frequency: e.target.value})}>
                {FREQUENCIES.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <Input type="date" mono value={d.start_date ?? ""} onChange={e => setD({...d, start_date: e.target.value})} />
            </Field>
            <Field label="End date (if known)">
              <Input type="date" mono value={d.end_date ?? ""} onChange={e => setD({...d, end_date: e.target.value})} />
            </Field>
          </div>
          <Field label="Payment method">
            <Select value={d.payment_method ?? ""} onChange={e => setD({...d, payment_method: e.target.value})}>
              <option value="">— not specified —</option>
              <option value="direct_debit">Direct debit</option>
              <option value="credit_card">Credit card</option>
              <option value="bank_transfer">Bank transfer (manual)</option>
              <option value="cheque">Cheque</option>
            </Select>
          </Field>
          {/* mig 386 — tie a standing cost to the premises Xero account it pays
              (rent → 469) so Commercial > Premises forecasts next year from the
              schedule, not from last year's bills. Optional: leave it as none for
              anything that isn't a premises cost. */}
          <Field label="Premises account" hint="Only for premises costs. The Xero account this standing cost is billed to (rent → 469 Rent), so the Premises forecast uses it.">
            <Select value={d.premises_xero_account ?? ""} onChange={e => setD({...d, premises_xero_account: e.target.value || null})}>
              <option value="">\u2014 not a premises cost \u2014</option>
              {(premisesCodes ?? []).map((c: any) => (
                <option key={c.xero_account_code} value={c.xero_account_code}>{c.xero_account_code} · {c.xero_account_name}</option>
              ))}
            </Select>
          </Field>

          <Field label="Notes"><Textarea rows={2} value={d.notes ?? ""} onChange={e => setD({...d, notes: e.target.value})} /></Field>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} onClick={() => onSave(d)} disabled={!d.item_name || !d.amount_ex_gst || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
